import React, { useMemo } from 'react';
import { getLearningPathByUserId, getUnitsByLearningPathId, calculateUnitProgress } from '../data/learningPathsData';
import { useSupabaseData } from '../hooks/useSupabaseData';

const LearningPathPage = ({ userId, language = 'traditional', onBack, onUnitClick }) => {
  const learningPathLocal = getLearningPathByUserId(userId);
  const unitsLocal = getUnitsByLearningPathId(learningPathLocal?.id);

  const { data: remote } = useSupabaseData({
    table: 'eng_learningpaths',
    mapRow: (row) => row,
    getFallback: () => null,
    query: {
      // eq: userId ? { user_id: userId } : undefined
    }
  });

  const { learningPath, units } = useMemo(() => {
    if (!remote || (Array.isArray(remote) && remote.length === 0)) {
      return { learningPath: learningPathLocal, units: unitsLocal };
    }
    const rows = Array.isArray(remote) ? remote : [remote];
    // Assume rows include path meta and units in separate rows identified by type
    const outUnits = [];
    let outPath = null;
    rows.forEach((r) => {
      const type = r.type || r.section || null;
      if (type === 'path') {
        outPath = {
          id: r.path_id || r.id,
          title: { traditional: r.title_tc || r.title, simplified: r.title_sc || r.title, english: r.title_en || r.title },
          totalUnits: r.total_units || 0,
          totalLessons: r.total_lessons || 0,
          cefrLevel: r.cefr_level || 'A1',
          rating: r.rating || 0,
          points: r.points || 0,
          progress: { completedLessons: r.completed_lessons || 0 }
        };
      } else if (type === 'unit') {
        outUnits.push({
          id: r.unit_id || r.id,
          unitNumber: r.unit_number || r.order || outUnits.length + 1,
          title: { traditional: r.title_tc || r.title, simplified: r.title_sc || r.title, english: r.title_en || r.title },
          description: { traditional: r.description_tc || r.description, simplified: r.description_sc || r.description, english: r.description_en || r.description },
          totalLessons: r.total_lessons || 0,
          estimatedDuration: r.estimated_duration || '15 min',
          difficulty: r.difficulty || 'beginner',
          isUnlocked: r.is_unlocked !== false
        });
      }
    });
    return { learningPath: outPath || learningPathLocal, units: outUnits.length > 0 ? outUnits : unitsLocal };
  }, [remote, userId]);

  const getText = (textObj) => {
    return textObj[language] || textObj.traditional;
  };

  if (!learningPath) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
        <div className="px-6 py-8">
          <button onClick={onBack} className="text-blue-400 mb-4">
            ← 返回
          </button>
          <div className="text-center py-8">
            <p>學習路徑未找到</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <button onClick={onBack} className="text-white">
            ←
          </button>
          <div className="text-center flex-1">
            <h1 className="text-xl font-bold">
              {getText(learningPath.title)}
            </h1>
            <p className="text-sm text-gray-300 mt-1">
              {learningPath.totalUnits} Units • CEFR {learningPath.cefrLevel}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-yellow-400">⭐</span>
            <span className="text-sm">{learningPath.rating}</span>
            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-xs font-bold">
              {learningPath.points}
            </div>
          </div>
        </div>

        {/* Unit Overview */}
        <div className="bg-blue-600 rounded-lg p-4 mb-6">
          <div className="text-sm font-semibold mb-2">UNIT 1</div>
          <div className="text-xs text-blue-100 mb-2">
            {learningPath?.progress?.completedLessons}/{learningPath?.totalLessons} Lessons
          </div>
          <h2 className="text-lg font-bold mb-2">
            {getText(units[0]?.title)}
          </h2>
          <p className="text-sm text-blue-100">
            {getText(units[0]?.description)}
          </p>
        </div>

        {/* Units List */}
        <div className="space-y-4">
          {units.map((unit) => {
            const progress = calculateUnitProgress(unit.id);
            return (
              <div
                key={unit.id}
                className={`bg-gray-800/30 border border-gray-600 rounded-xl p-4 ${
                  unit.isUnlocked ? 'cursor-pointer hover:bg-gray-700/50' : 'opacity-50 cursor-not-allowed'
                }`}
                onClick={() => unit.isUnlocked && onUnitClick(unit.id)}
              >
                <div className="flex items-start space-x-4">
                  {/* Unit Icon */}
                  <div className="w-12 h-12 rounded-lg bg-blue-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {unit.unitNumber}
                  </div>
                  
                  {/* Unit Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-white">
                        {getText(unit.title)}
                      </h3>
                      <div className="text-sm text-gray-300">
                        {progress.completed}/{unit.totalLessons}
                      </div>
                    </div>
                    <p className="text-gray-400 text-sm mb-3">
                      {getText(unit.description)}
                    </p>
                    
                    {/* Progress Bar */}
                    <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progress.percentage}%` }}
                      ></div>
                    </div>
                    
                    {/* Unit Stats */}
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>{unit.estimatedDuration}</span>
                      <span className={`px-2 py-1 rounded ${
                        unit.difficulty === 'beginner' ? 'bg-green-500/20 text-green-300' :
                        unit.difficulty === 'intermediate' ? 'bg-yellow-500/20 text-yellow-300' :
                        'bg-red-500/20 text-red-300'
                      }`}>
                        {unit.difficulty.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  
                  {/* Status Icon */}
                  <div className="flex-shrink-0">
                    {progress.completed === unit.totalLessons ? (
                      <div className="text-green-500 text-xl">✓</div>
                    ) : progress.completed > 0 ? (
                      <div className="w-6 h-6 bg-green-500 rounded-full"></div>
                    ) : unit.isUnlocked ? (
                      <div className="w-6 h-6 bg-gray-500 rounded-full"></div>
                    ) : (
                      <div className="w-6 h-6 bg-gray-600 rounded-full border-2 border-gray-500"></div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LearningPathPage;
